import React from 'react'
import styled, {css} from 'styled-components'
import {Dialog as MuiDialog, DialogProps as MuiDialogProps} from '@mui/material'
import {X} from 'lucide-react'

export type DialogSize = 'sm' | 'md' | 'lg' | 'xl'

const SIZE_MAX_WIDTH: Record<DialogSize, string> = {
  sm: '420px',
  md: '560px',
  lg: '720px',
  xl: '960px',
}

// Shared title/header chrome so the plain title and the header-with-close
// variant are pixel-identical apart from the flex row + X.
const titleChrome = css`
  padding: var(--space-6) var(--space-6) var(--space-4);
  margin: 0;
  font-size: var(--fs-h3);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-tight);
  color: var(--text-primary);
  border-bottom: var(--border-thin, 1px) solid var(--border-subtle);
`

export const StyledDialogTitle = styled.div`
  ${titleChrome}
`

const DialogHeaderRow = styled.div`
  ${titleChrome}
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-4);
`

const DialogHeaderTitle = styled.h2`
  min-width: 0;
  margin: 0;
  font: inherit;
  color: inherit;
  display: flex;
  align-items: center;
  gap: var(--space-2);
`

const DialogCloseButton = styled.button`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: none;
  background: transparent;
  border-radius: var(--radius-sm);
  color: var(--text-secondary);
  cursor: pointer;
  transition: background var(--motion-fast) var(--motion-ease);

  &:hover {
    background: var(--bg-hover);
    color: var(--text-primary);
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--focus-ring);
  }
`

export const StyledDialogContent = styled.div`
  padding: var(--space-6);
  overflow-y: auto;
  font-size: var(--fs-body);
  line-height: var(--lh-normal);
  color: var(--text-primary);

  /* Remove default margin from first/last children */
  > *:first-child {
    margin-top: 0;
  }

  > *:last-child {
    margin-bottom: 0;
  }
`

export const StyledDialogActions = styled.div`
  padding: var(--space-4) var(--space-6) var(--space-6);
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: var(--space-3);
  border-top: var(--border-thin, 1px) solid var(--border-subtle);
  background-color: var(--bg-elevated);

  /* Responsive stacking on small screens */
  @media (max-width: 480px) {
    flex-direction: column-reverse;

    & > * {
      width: 100%;
    }
  }
`

// Wrapper for the MUI Dialog with DS-token styling.
export const StyledDialog = styled(MuiDialog)<{$size?: DialogSize; $fullWidthPaper?: boolean}>`
  .MuiDialog-paper {
    border-radius: var(--radius-lg);
    box-shadow: var(--shadow-modal);
    background-color: var(--bg-elevated);
    width: 100%;
    margin: var(--space-8);

    ${(p) =>
      p.$fullWidthPaper
        ? css`
            /* Wide dialogs (document/image previews, wide data layouts) —
               fills the viewport up to a generous cap, matching legacy
               full-width paper. */
            max-width: min(95vw, 1600px);
          `
        : p.$size
        ? css`
            max-width: ${SIZE_MAX_WIDTH[p.$size]};
          `
        : css`
            /* Legacy default — unchanged for dialogs that don't opt into a size */
            max-width: min(90vw, 600px);
          `}

    @media (max-width: 600px) {
      margin: var(--space-4);
      max-width: calc(100vw - 32px);
    }
  }

  .MuiBackdrop-root {
    background-color: var(--overlay-backdrop);
  }
`

// ---- Sub-part prop interfaces ----

export interface StyledDialogTitleProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode
  className?: string
  /** When set, the title renders as a flex header with a right-flexed close (X) that calls this. */
  onClose?: () => void
  /** Suppress the close (X) even when onClose is set — for destructive/blocking confirms. */
  hideClose?: boolean
}
/** Alias — DialogTitle IS the header now. */
export type DialogTitleProps = StyledDialogTitleProps

export interface StyledDialogContentProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode
  className?: string
}

export interface StyledDialogActionsProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode
  className?: string
}

/**
 * Dialog title / header. Pass `onClose` to get the standard right-flexed close
 * (X) for free (shown by default); pass `hideClose` to suppress it on blocking
 * confirms. Without `onClose` it renders as a plain title block (backward-compatible).
 */
export const DialogTitle: React.FC<StyledDialogTitleProps> = ({
  children,
  className,
  onClose,
  hideClose,
  ...rest
}) => {
  if (onClose && !hideClose) {
    return (
      <DialogHeaderRow className={className} {...rest}>
        <DialogHeaderTitle>{children}</DialogHeaderTitle>
        <DialogCloseButton type="button" aria-label="Close" onClick={onClose}>
          <X size={20} />
        </DialogCloseButton>
      </DialogHeaderRow>
    )
  }
  return (
    <StyledDialogTitle className={className} {...rest}>
      {children}
    </StyledDialogTitle>
  )
}

/** Semantic alias for new code — identical to DialogTitle. */
export const DialogHeader = DialogTitle

export const DialogContent: React.FC<StyledDialogContentProps> = ({
  children,
  className,
  ...rest
}) => (
  <StyledDialogContent className={className} {...rest}>
    {children}
  </StyledDialogContent>
)

export const DialogActions: React.FC<StyledDialogActionsProps> = ({
  children,
  className,
  ...rest
}) => (
  <StyledDialogActions className={className} {...rest}>
    {children}
  </StyledDialogActions>
)

// ---- Dialog root ----

export interface DialogProps extends MuiDialogProps {
  children: React.ReactNode
  /** Width preset: sm=420 / md=560 / lg=720 / xl=960. When omitted, legacy width is preserved. */
  size?: DialogSize
  /** Escape hatch for document/image previews — paper grows to min(90vw, 1000px). */
  fullWidthPaper?: boolean
}

// Re-export the styled Dialog as the main Dialog component with proper typing.
export const Dialog: React.FC<DialogProps> = ({size, fullWidthPaper, children, ...rest}) => (
  <StyledDialog $size={size} $fullWidthPaper={fullWidthPaper} {...rest}>
    {children}
  </StyledDialog>
)

export default {
  Dialog,
  DialogHeader,
  DialogTitle,
  DialogContent,
  DialogActions,
}
